import {useState, useEffect} from 'react';
import { Card, Box, Divider } from "@mui/material";
import { BasicStructure, PreOrderData, PreOrderValidationError, SessionData } from "../../common/Interfaces";
import TitleSubtitle from "../common/formControls/titleSubtitle/TitleSubtitle";
import Combobox from "../common/formControls/combobox/Combobox";
import InputTextField from "../common/formControls/textField/InputTextField";
import styles from './ProductData.module.scss';
import { useTranslation } from "react-i18next";
import { getProductTypes } from "../../common/requests/ProductTypesRequests";
import { getProductBrands } from '../../common/requests/ProductBrandsRequests';
import InputDateTextField from '../common/formControls/textField/InputDateTextField';
import { getDateInYYYYMMDDFormat } from '../../common/Utils';

interface Props{
    auth: SessionData;
    preOrder: PreOrderData;
    setPreOrderData: any;
    preOrderValidations: PreOrderValidationError;
}

export default function ProductData ({preOrder, setPreOrderData, preOrderValidations, auth}: Props){
    const {t} = useTranslation();
    const controlsWidth = "100%";
    const [productTypes, setProductTypes] = useState<BasicStructure[]>([]);
    const [productBrands, setProductBrands] = useState<BasicStructure[]>([]);
    const preOrderHasId = preOrder?.Id && preOrder.Id > 0;

    const getProductLocationOptions = () => [
        {Id: 'PRODUCTO SE ENCUENTRA EN DOMICILIO DEL CLIENTE',
            Name: 'PRODUCTO SE ENCUENTRA EN DOMICILIO DEL CLIENTE'},
        {Id: 'PRODUCTO SE ENCUENTRA EN PDV',
        Name: 'PRODUCTO SE ENCUENTRA EN PDV'},
    ]

    useEffect(() => {
        if(!preOrderHasId) obtainsParameters();
    }, []);

    const obtainsParameters = async () => {
        const productTypesResult = await getProductTypes(auth.token ?? '', auth.sessionData.ManagementId);
        setProductTypes(productTypesResult ?? []);

        const productBrandsResult = await getProductBrands(auth.token ?? '', auth.sessionData.ManagementId);
        setProductBrands(productBrandsResult ?? []);
    }

    const handleProductValueChange = (e: any) => {
        setPreOrderData({
            ...preOrder,
            [e.target.name]: e.target.value,
        });
    }

    const handleProductBillDateValueChange = (value: Date) => {
        setPreOrderData({
            ...preOrder,
            BillDate: getDateInYYYYMMDDFormat(value),
        })
    }

    const handleProductType = (_e: any, value: any) => {
        setPreOrderData({
            ...preOrder,
            ProductType: value,
        });
    }

    const handleProductBrand = (_e: any, value: any) => {
        setPreOrderData({
            ...preOrder,
            ProductBrand: value,
        });
    }

    const handleProductLocationChange = (_e: any, value: any) => {
        setPreOrderData({
            ...preOrder,
            ProductLocation: !value ? undefined : value.Name,
        });
    }

    return (
        <Card className={[styles.fullwidth, styles.container].join(" ")}>
            <Box className={[styles.fullwidth, styles.verticalContainer].join(" ")}>
                <TitleSubtitle title={t("PRODUCTDATA")}/>
                <Divider/>
                <Box className={styles.verticalContainer}>
                    {preOrder.Id === 0 ? (
                        <Combobox fieldId={"ProductType"} 
                        handleValueChange={handleProductType} 
                        fieldValue={preOrder.ProductType} 
                        fieldOptions={productTypes} 
                        width={controlsWidth}
                        errorMessage={preOrderValidations.ProductType}
                        fieldLabel={t("PRODUCTTYPE")}/>
                    ) : (
                        <InputTextField id={"ProductType"} 
                                    label={t("PRODUCTTYPE")} 
                                    value={preOrder.ProductType?.Name ?? ''} 
                                    handleValueChange={undefined}
                                    width={controlsWidth}
                                    errorMessage={preOrderValidations.ProductType}
                                    isReadOnly={preOrder.Id > 0}/>
                    )}
                    
                    {preOrder.Id === 0 ? (
                        <Combobox fieldId={"ProductBrand"} 
                        handleValueChange={handleProductBrand} 
                        fieldValue={preOrder.ProductBrand} 
                        fieldOptions={productBrands} 
                        width={controlsWidth}
                        errorMessage={preOrderValidations.ProductBrand}
                        fieldLabel={t("BRANDANDSERVICE")}/>
                    ) : (
                        <InputTextField id={"ProductBrand"} 
                                    label={t("BRANDANDSERVICE")} 
                                    value={preOrder.ProductBrand?.Name ?? ''} 
                                    handleValueChange={undefined}
                                    width={controlsWidth}
                                    errorMessage={preOrderValidations.ProductBrand}
                                    isReadOnly={preOrder.Id > 0}/>
                    )}                    

                    <InputTextField id={"ProductModel"} 
                                    label={t("MODEL")} 
                                    value={preOrder.ProductModel ?? ''} 
                                    handleValueChange={handleProductValueChange}
                                    width={controlsWidth}
                                    errorMessage={preOrderValidations.ProductModel}
                                    isReadOnly={preOrder.Id > 0}
                                    isOptional/>

                    <InputTextField id={"ProductSerial"} 
                                    label={t("SERIAL")} 
                                    value={preOrder.ProductSerial ?? ''} 
                                    handleValueChange={handleProductValueChange}
                                    width={controlsWidth}
                                    errorMessage={preOrderValidations.ProductSerial}
                                    isReadOnly={preOrder.Id > 0}
                                    isOptional/>

                    <InputTextField id={"BillNumber"}
                                    label={t("BILLNUMBER")} 
                                    value={preOrder.BillNumber ?? ''} 
                                    handleValueChange={handleProductValueChange}
                                    width={controlsWidth}
                                    errorMessage={preOrderValidations.BillNumber}
                                    isReadOnly={preOrder.Id > 0}
                                    isOptional/>

                    {preOrder.Id > 0 ? (
                        <InputTextField id={"BillDate"}
                        label={t("BILLDATE")} 
                        value={preOrder.BillDate ? getDateInYYYYMMDDFormat(new Date(preOrder.BillDate)) : ''} 
                        handleValueChange={handleProductValueChange}
                        width={controlsWidth}
                        errorMessage={preOrderValidations.BillDate}
                        isReadOnly={preOrder.Id > 0}
                        isOptional/>
                    ) : (
                        <InputDateTextField id={"BillDate"}
                        label={t("BILLDATE")} 
                        value={preOrder.BillDate ?? ''} 
                        handleValueChange={handleProductBillDateValueChange}
                        width={controlsWidth}
                        errorMessage={preOrderValidations.BillDate}
                        isReadOnly={preOrder.Id > 0}
                        isOptional/>
                    )}

                    {preOrder.Id === 0 && (
                        <Combobox fieldId={"ProductLocation"} 
                        handleValueChange={handleProductLocationChange} 
                        fieldValue={preOrder.ProductLocation} 
                        fieldOptions={getProductLocationOptions()} 
                        width={controlsWidth}
                        errorMessage={preOrderValidations.ProductLocation}
                        fieldLabel={t("PRODUCTLOCATION")}/>
                    )}
                    
                </Box>
            </Box>
        </Card>
    );
}